import React, {PropsWithChildren, useEffect} from "react";
import {useSearchParams} from "react-router";
import {useErrorBoundary} from "react-error-boundary";
import {useMapsLibrary} from "@vis.gl/react-google-maps";
import getLatLngLiteralForPlace from "../functions/getLatLngLiteralForPlace";
import {BljStoreLocatorConfig} from "../types/BljStoreLocatorConfig";
import handleWPAjaxRequest from "../functions/handleWPAjaxRequest";
import WPResponse from "../types/WPResponse";
import StoreSearchResult from "../types/StoreSearchResult";
import {useDispatch} from "react-redux";
import {AppDispatch} from "../store";
import {isSearchFormLoadingSliceActions} from "../store/isSearchFormLoadingSlice";
import {isSearchOccurredSliceActions} from "../store/isSearchOccurredSlice";
import {storeSearchCircleSliceActions} from "../store/storeSearchCircleSlice";
import {searchResultsSliceActions} from "../store/searchResultsSlice";
import locationDefinitionModeSlice from "../store/locationDefinitionModeSlice";

declare const bljStoreLocatorConfig: BljStoreLocatorConfig;

const StoreLocatorRoot: React.FC<PropsWithChildren> = (props) => {

    const [searchParams] = useSearchParams();
    const {showBoundary} = useErrorBoundary<Error>();
    const placesLib = useMapsLibrary("places");

    const dispatch = useDispatch<AppDispatch>();

    const processFilteringRequest = (
        locationDefinitionMode: "manual" | "gps",
        storeSearchCenter: google.maps.LatLngLiteral | null,
        storeSearchRadius: string | null,
        storeProductTypes: string | null
    ) => {

        if (storeSearchCenter === null) {

            if (locationDefinitionMode === "manual") {
                throw new Error("No location data found for the given place ID.");
            } else {
                throw new Error("No user location found in the search parameters.");
            }

        }

        if (storeSearchRadius === null) {
            throw new Error("No search radius found in the search parameters.");
        }

        if (storeProductTypes === null) {
            throw new Error("No product types found in the search parameters.");
        }


        // Set the store search center coordinates
        dispatch(storeSearchCircleSliceActions.setStoreSearchCenterCoords(storeSearchCenter));

        // Loading of the Search Results
        const selectedProductTypesParsed = storeProductTypes.split(",");

        const requestBody = new FormData();
        requestBody.append("action", "blj_filter_stores");
        requestBody.append("stores_filtration_nonce", bljStoreLocatorConfig.bljStoreLocatorFilterStoresNonce);
        requestBody.append("store_search_center", JSON.stringify(storeSearchCenter));
        requestBody.append('store_search_radius', storeSearchRadius);
        requestBody.append("store_product_types", JSON.stringify(selectedProductTypesParsed));

        dispatch(isSearchFormLoadingSliceActions.setIsSearchFormLoading(true));

        handleWPAjaxRequest(requestBody)
            .then((response) => response.json())
            .then((responseData: WPResponse<StoreSearchResult[]>) => {

                if (!responseData.success) {
                    throw new Error(responseData.data.message);
                }

                // Update the stores on the map and in the list
                dispatch(searchResultsSliceActions.setSearchResults(responseData.data));

                // Set the search radius
                dispatch(storeSearchCircleSliceActions.setSearchCircleRadiusInMiles(+storeSearchRadius));

                // Set the search form loading state to false
                dispatch(isSearchFormLoadingSliceActions.setIsSearchFormLoading(false));

                // Set the search occurred state to true
                dispatch(isSearchOccurredSliceActions.setIsSearchOccurred(true));
            })
            .catch((error: Error) => {
                console.error(error);

                // Set the search form loading state to false
                dispatch(isSearchFormLoadingSliceActions.setIsSearchFormLoading(false));
                dispatch(isSearchOccurredSliceActions.setIsSearchOccurred(false));
                dispatch(searchResultsSliceActions.setSearchResults([]));


                // Showing an error message to the user
                showBoundary(error);
            });

    }

    // Loading Search Results depending on the search parameters changes in the URL
    useEffect(() => {

        if (!placesLib) {
            return;
        }

        const locationDefinitionMode = searchParams.get("location_definition_mode");
        if (locationDefinitionMode === null) {
            return;
        }

        const searchRadiusInMiles = searchParams.get("store_search_radius");
        const selectedProductTypes = searchParams.get("store_product_types");

        // If the location definition mode is manual (user entered the place in the Field)
        if (locationDefinitionMode === "manual") {

            dispatch(locationDefinitionModeSlice.actions.setLocationDefinitionMode("manual"));

            const storeSearchPlaceId = searchParams.get("store_search_place_id");

            if (storeSearchPlaceId === null) {
                console.warn("No place ID found in the search parameters.");
                throw new Error("No place ID found in the search parameters.");
            }

            getLatLngLiteralForPlace(placesLib, storeSearchPlaceId)
                .then((latLngLiteral) => {

                    processFilteringRequest(locationDefinitionMode, latLngLiteral, searchRadiusInMiles, selectedProductTypes);

                })
                .catch((error: Error) => {
                    console.error(error);
                    showBoundary(error);
                });

        } else if (locationDefinitionMode === "gps") {
            // If the location definition mode is GPS (user allowed the browser to use the location)

            const storeSearchUserLocation = searchParams.get("store_search_user_location");

            dispatch(locationDefinitionModeSlice.actions.setLocationDefinitionMode("gps"));

            if (storeSearchUserLocation === null) {
                throw new Error("No user location found in the search parameters.");
            }

            const storeSearchUserLocationParsed = storeSearchUserLocation.split(",");
            const storeSearchUserLocationObj = {
                lat: parseFloat(storeSearchUserLocationParsed[0]),
                lng: parseFloat(storeSearchUserLocationParsed[1])
            };

            processFilteringRequest(locationDefinitionMode, storeSearchUserLocationObj, searchRadiusInMiles, selectedProductTypes);

        } else {
            console.warn("No correct location definition mode found in the search parameters.");
            showBoundary(new Error("No correct location definition mode found in the search parameters."));
        }

    }, [placesLib, searchParams]);


    return (
        <section className="blj-store-locator">
            {props.children}
        </section>
    );

}

export default StoreLocatorRoot;